import { Breadcrumb, Input, LoadingSpinner } from "@/components";
import { useDebounce } from "@/hooks/useDebounce";
import { cn } from "@/utils";
import { useEffect, useRef, useState } from "react";
import { HiArrowLeft } from "react-icons/hi";
import { IoIosSearch } from "react-icons/io";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { useDoneQuizCategoryList } from "../hooks/useQuizList";
import { DoneQuizModal } from "./DoneQuizModal";

const BLANK_VALUE = "_";
const SEARCH_PARAMS = {
  SEARCH: "s",
};

const DoneQuizCategory = () => {
  const isAtBottomRef = useRef(false);
  const refQuiz = useRef<HTMLElement>(null);
  const navigation = useNavigate();
  const { id } = useParams<{ id: string }>();
  const isInitialMount = useRef(true);
  const [searchParams, setSearchParams] = useSearchParams();
  const categoryId = Number(id);
  const params = Object.fromEntries([...searchParams]);
  const currentSearch = params[SEARCH_PARAMS.SEARCH] || "";
  const [inputValue, setInputValue] = useState<string>(currentSearch);
  const debouncedSearchTerm = useDebounce(inputValue, 500);

  const {
    data: categoriesData,
    isLoading,
    hasNextPage,
    fetchNextPage,
  } = useDoneQuizCategoryList(categoryId, currentSearch);

  const updateSearchParams = (object: { [key: string]: string }) => {
    const inputParams: { [key: string]: string } = params;
    for (const key in object) {
      const value = object[key]?.trim();
      if (value) {
        inputParams[key] = value;
      } else {
        delete inputParams[key];
      }
    }
    setSearchParams(inputParams, { replace: true });
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter") {
      updateSearchParams({
        [SEARCH_PARAMS.SEARCH]: inputValue?.trim(),
      });
    }
  };

  useEffect(() => {
    // Skip updating search params on initial mount
    if (isInitialMount.current) {
      isInitialMount.current = false;
      return;
    }

    if (debouncedSearchTerm?.trim()) {
      updateSearchParams({
        [SEARCH_PARAMS.SEARCH]: debouncedSearchTerm?.trim() || "",
      });
    } else {
      updateSearchParams({ [SEARCH_PARAMS.SEARCH]: "" });
    }
  }, [debouncedSearchTerm]);

  useEffect(() => {
    if (!hasNextPage) return;
    window.addEventListener("scroll", checkIfAtBottom);
    return () => {
      window.removeEventListener("scroll", checkIfAtBottom);
    };
  }, [hasNextPage]);

  const handleBack = () => {
    navigation(-1);
  };

  const checkIfAtBottom = () => {
    refQuiz.current = window.document.getElementById("root");
    if (!refQuiz.current) return;
    const { scrollTop, scrollHeight, clientHeight } = refQuiz.current;
    const isAtBottom = scrollHeight - scrollTop <= clientHeight + 5; // Ngưỡng 5px

    if (isAtBottom && !isAtBottomRef.current) {
      isAtBottomRef.current = true;
      if (hasNextPage) {
        fetchNextPage();
      }
    } else if (!isAtBottom) {
      isAtBottomRef.current = false;
    }
  };

  const quizListFlat =
    categoriesData?.pages.flatMap((page) => page?.data?.data || []) || [];
  const categoryInfo = categoriesData?.pages?.[0]?.data?.category;

  return (
    <section>
      <div className="py-4 space-y-2">
        <Breadcrumb
          breadcrumbs={[
            { label: "Eラーンニング", path: "/e-learning" },
            { label: categoryInfo?.title || "-", path: "" },
          ]}
        />
        <button
          className="flex items-center gap-2 text-base font-bold leading-7 hover:text-cyan"
          onClick={handleBack}
        >
          <HiArrowLeft className="w-5 h-5 hover:text-cyan" />{" "}
          <p className="max-w-96 truncate">
            {categoryInfo?.title || BLANK_VALUE}
          </p>
        </button>
      </div>
      <Input
        value={inputValue}
        onChange={(e) => setInputValue(e.target.value)}
        placeholder="学習名で検索"
        icon={<IoIosSearch className="size-4" />}
        className="!h-9 !w-80"
        onKeyDown={handleKeyDown}
        containerClassName="mb-4"
      />

      <div className={cn("space-y-4 text-dark w-full")}>
        <div className="flex items-center justify-between sticky">
          <h2 className="font-semibold leading-6 truncate max-w-[500px]">
            {categoryInfo?.title || BLANK_VALUE}
          </h2>
        </div>

        {isLoading && categoriesData === undefined && (
          <LoadingSpinner className="mt-10" />
        )}
        <div
          className={cn(
            "grid grid-cols-4 2xl:grid-cols-5 gap-6 overflow-y-auto"
          )}
        >
          {(quizListFlat || []).map((quiz) => (
            <DoneQuizModal key={quiz.id} quiz={quiz} />
          ))}
        </div>
      </div>
    </section>
  );
};

export default DoneQuizCategory;
